/**
 * Group model
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import UsersCollection from '../collections/users';

export default class Group extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'groupId';
	}

	/** @type {string} */
	get titleAttribute() {
		return 'name';
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object}  defaults            - The default attribute values
	 * @property {number}  defaults.groupId    - The default system group id
	 * @property {string}  defaults.name       - The default system group name
	 * @property {array}   defaults.memberIds  - The defualt array of system group users
	 *
	 * @todo it's unfortunate that this uses 'memberIds' and not 'userIds'. Refactor?
	 */
	get defaults() {
		return {
			//groupId: 0,
			name: '',
			memberIds: [],
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['members'];
	}

	/**
	 * @property {object} methods                  - The methods available on this model
	 * @property {method} methods.create           - The create method (post)
	 * @property {method} methods.read             - The read method (get)
	 * @property {method} methods.remove           - The destroy method (delete)
	 * @property {method} methods.update           - The update method (put)
	 *
	 * @property {method} methods.getGroupObjects  - Fetches group objects by id
	 * @property {method} methods.getUserIds       - Fetches user ids for system group
	 * @property {method} methods.getUsers         - Fetches users for system group
	 * @property {method} methods.setUserIds       - Sets users on system group
	 * @property {method} methods.exportData       - Exports system group user data
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create: function (opts) {
				return ['setGroup', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read: function (opts) {
				// We do not have a call to get a single group
				// We do have a call to get groups by id, and so we use that and fix the array return part
				return this.getGroupObjects(opts).then(function (groups) {
					if (!groups.length) return null;

					if (!opts.ignoreUsers) {
						return this.getUsers(opts).then(function () {
							return groups[0];
						});
					}
					return groups[0];
				});
			},
			/** @method */
			remove(opts) {
				return ['deleteGroup', this.id];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			update: function (opts) {
				return [
					'setGroup',
					{
						groupId: this.id,
						name: this.get('name'),
					},
				];
			},
			// Helpers
			autolist(name, opts) {
				return ['getGroupAutoList', name];
			},
			// downloadUserData takes a `UserSelector` object - though it currently only supports all, none and groupIds
			// See: package com.netxposure.module.user.selector.UserSelector for more deails on this object
			// TODO: once this object is fully supported we should mirror it with our own model - then we could pass params and get a valid `UserSelector` to send
			// to any call that will expect one
			downloadUserData: function (opts) {
				return ['downloadUserData', {groupIds: [this.id]}];
			},
			/** @method */
			getByName(name, opts) {
				return this.autolist(name, opts).then((results) => {
					const result = results.find((result) => result.label === name);
					if (!result) {
						return new Error(`Group "${name}" not found`);
					}
					this.set({[this.idAttribute]: parseInt(result.value)});
					return this.fetch();
				});
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getGroupObjects: function (opts) {
				return ['getGroupObjects', [this.id]];
			},
			/** @method */
			getUserIds(opts) {
				return ['getUsersForGroup', this.id];
			},
			/**
			 * @method
			 * @deprecated See `this.users`
			 */
			getUsers: ['getUserObjects', 'memberIds'],
			/** @method */
			setUserIds(opts) {
				return ['setGroupUsers', this.id, this.get('memberIds')];
			},
		};
	}

	/**
	 * @property {object}   parsers            - The parsers available on this model
	 * @property {function} parsers.create     - Parses result of create method
	 * @property {function} parsers.update     - Parses result of update method
	 * @property {function} parsers.getUserIds - Parses result of fetched user ids for system group
	 * @property {function} parsers.getUsers   - Parses result of fetched users for system group
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {object} response Response
			 * @param {object} opts Request options
			 * @todo return full beans
			 */
			create: function (response, opts) {
				// We just get that messy validation object
				return {
					groupId: response.id,
				};
			},
			/**
			 * @function
			 * @param {object} response Response
			 * @param {object} opts Request options
			 * @todo return full updated beans
			 */
			update: function (response, opts) {
				// TODO: We were setting the id again to avoid the backbone code
				// to add that whole messy validation object to our model
				// our id will never change after a creation and we can just
				// return `null` until we are getting back useful data from an update
				// this.set( { groupId: response.id }, opts );
				return null;
			},
			/**
			 * @function
			 * @param {array} userIds User ids
			 * @param {object} opts Request options
			 */
			getUserIds: function (userIds, opts) {
				this.set('memberIds', userIds, opts);
			},
		};
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		/**
		 * @memberof module:user/models/group
		 * @alias users
		 * @type {module:user/collections/users}
		 */
		this.users = new UsersCollection([], {
			// Used in a selection mode our labels get tangled
			// see userProfile
			comparator: function (a, b) {
				return a.getAutocompleteLabel() < b.getAutocompleteLabel() ? -1 : 1;
			},
			groupId: this.id,
			// We do not have a good solution for paging selected items as of yet
			pager: {
				pageSize: 0x7fffffff,
			},
		});

		// Hey, listen!
		this.listenTo(this, 'change:groupId', function (model) {
			this.users.groupId = this.id;
		});
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	fetchUsers(opts) {
		return this.users.fetch(
			_.extend(
				{
					duplicateQueryPolicy: 'abort',
					reset: true,
				},
				opts,
			),
		);
	}

	/**
	 * @method
	 * @param {object} data Data
	 * @param {object} opts Options
	 * @return {boolean} Members updated
	 */
	parseAndSave(data, opts) {
		// Save changes
		return this.save(
			{
				name: data.name,
				memberIds: data.ids,
			},
			_.extend({wait: true}, opts),
		).then(function () {
			// currently setGroupUsers(setUserIds) returns
			// errors that setGroup(setUserIds) does not.
			// so we must call this every time
			return this.setUserIds().then(
				() => {
					return true;
				},
				(e) => {
					this.fetchUsers();
					throw e;
				},
			);
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'Group';
	}
}
